import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
    CardDto,
    CardService,
    CardState,
    CustomRoleDto,
    EwalletDto,
    PageableRequestDto,
    PagedResultDtoOfCustomerAuditEntryDto,
    Title,
    CustomerAuditEntryDto,
    CustomerAuditEntryPropertyChangeType,
    CustomerInputDto,
    CustomerMeInputDto,
    CustomerService, VirtualCardDto,
    SiteDto,
} from '../../../_services/configuration-services';
import {ActivatedRoute, Router} from '@angular/router';
import {NotificationsService} from '../../../_shared/notifications.service';
import {Location} from '@angular/common';
import {NgbDateParserFormatter, NgbDateStruct, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CustomerRfidCardModalComponent} from './customer-rfid-card-modal/customer-rfid-card-modal.component';
import {faCalendar, faPen, faQrcode, faRotate, faTimes, faUserSlash, faWallet} from '@fortawesome/free-solid-svg-icons';
import {environment} from '../../../../environments/environment';
import {MsalService} from '@azure/msal-angular';
import {FormUtils} from '../../../_shared/form-utils';
import {DateUtils} from '../../../_shared/date-utils';
import {ConfirmModalService} from '../../../_shared/_components/confirm-modal/confirm-modal.component';
import {CustomDateFormatter} from '../../../_shared/custom-date-formatter';
import {CssConstants, PermissionKeys} from '../../../app.constants';
import {
    CustomerDeleteConfirmationModalComponent
} from './customer-delete-confirmation-modal/customer-delete-confirmation-modal.component';
import {filter, firstValueFrom, Subscription} from 'rxjs';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {TranslateUtils} from '../../../_shared/translate-utils';
import {SiteUtils} from '../../../_shared/site-utils';
import {CustomerConfigService} from '../../../_shared/customer-config-service';
import {RfidCardModalComponent} from '../../../_shared/_components/rfid-card-modal/rfid-card-modal.component';
import {RolesService} from '../../../_shared/roles-service';
import {
    VirtualCardQrCodeViewComponent
} from '../../../_shared/_components/virtual-card-qr-code-view/virtual-card-qr-code-view.component';
import { BackofficePermissionsService } from 'src/app/_shared/backoffice-permissions.service';

@Component({
    selector: 'app-customer-management',
    templateUrl: './customer-management.component.html',
    providers: [
        { provide: NgbDateParserFormatter, useClass: CustomDateFormatter }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomerManagementComponent implements OnInit, OnDestroy {

    CustomerAuditEntryPropertyChangeType = CustomerAuditEntryPropertyChangeType;
    PermissionKeys = PermissionKeys;
    textInputPattern = FormUtils.textInputPattern;
    dateTimeFormat = DateUtils.dateTimeFormat;

    customerForm: FormGroup<CustomerForm>;

    customerId: string | null = null;
    editingMyProfile = false;
    householdSizeDisplayed = false;

    ewallet?: EwalletDto = null;
    cards: CardDto[] = [];

    // icons
    icons = {
        calendar: faCalendar,
        times: faTimes,
        userSlash: faUserSlash,
        edit: faPen,
        wallet: faWallet,
        qrCode: faQrcode,
        generate: faRotate
    };

    private langChangeSubscription: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    pagedAudits: PagedResultDtoOfCustomerAuditEntryDto | null = null;
    auditCurrentPage = 0;
    auditPageSize = 10;

    constructor(
        private readonly appRoleService: RolesService,
        private readonly appPermissionService: BackofficePermissionsService,
        private readonly customerService: CustomerService,
        private readonly router: Router,
        private readonly notificationsService: NotificationsService,
        private readonly modalService: NgbModal,
        private readonly route: ActivatedRoute,
        private readonly confirmService: ConfirmModalService,
        private readonly authService: MsalService,
        private readonly location: Location,
        private readonly cardService: CardService,
        private readonly translateService: TranslateService,
        private readonly customerConfigService: CustomerConfigService) {

        this.customerForm = new FormGroup<CustomerForm>({
                id: new FormControl<string | null>(null),
                title: new FormControl<Title | null>(Title.NotSpecified, [Validators.required]),
                firstname: new FormControl<string | null>(null, [Validators.required]),
                lastname: new FormControl<string | null>(null, [Validators.required]),
                birthDate: new FormControl<NgbDateStruct | null>(null, [FormUtils.datePatternValidator]),
                email: new FormControl<string | null>(null, [Validators.required, FormUtils.emailValidator]),
                phone: new FormControl<string | null>(null, [FormUtils.phoneNumberPatternValidator]),
                mobilePhone: new FormControl<string | null>(null, [FormUtils.phoneNumberPatternValidator]),
                businessPhone: new FormControl<string | null>(null, [FormUtils.phoneNumberPatternValidator]),
                street: new FormControl<string | null>(null, [Validators.required]),
                postalCode: new FormControl<string | null>(null, [Validators.required]),
                city: new FormControl<string | null>(null, [Validators.required]),
                country: new FormControl<string | null>(null, [Validators.required, Validators.pattern(/^[a-zA-Z ]*$/)]),
                customerNumber: new FormControl<string | null>(null, [Validators.required]),
                customRoles: new FormControl<CustomRoleDto[]>([]),
                sites: new FormControl<SiteDto[]>([]),
                attentionTo: new FormControl<string | null>(null),
                householdSize: new FormControl<number | null>(null, [Validators.min(1), FormUtils.integerPatternValidator]),
                accountEnabled: new FormControl<boolean | null>(true, {validators: Validators.required}),
                comment: new FormControl<string | null>(null),
                internalField1: new FormControl<string | null>(null),
                internalField2: new FormControl<string | null>(null),
                internalField3: new FormControl<string | null>(null),
                internalField4: new FormControl<string | null>(null),
                internalField5: new FormControl<string | null>(null),
            },
            {updateOn: 'change'}
        );
    }

    ngOnInit(): void {
        this.customerId = this.route.snapshot.params['id'] ?? null;

        this.crtLang = this.translateService.currentLang;

        this.langChangeSubscription = this.translateService.onLangChange.subscribe(async (event: LangChangeEvent) => {
            this.crtLang = event.lang;
            if (this.editingMyProfile) {
                this.sites.setValue(SiteUtils.sortByName(this.sites.value, this.crtLang));
            }
        });

        if (this.customerId) { // Edit Customer (Admin)

            this.customerService.getCustomer(this.customerId).pipe().subscribe({
                next: customer => {
                    this.cards = customer.cards ?? [];

                    if (this.appPermissionService.hasPermission(PermissionKeys.gestionTransactions)) {
                        this.customerService.getEwalletByCustomerId(this.customerId).pipe().subscribe(w => this.ewallet = w);
                    }

                    void this.setupFormToEditFullProfile(customer);
                    void this.setupHouseholdSize();
                },
                error: () => {
                    this.back();
                    return null;
                }
            });

        } else if (this.route.routeConfig.path.endsWith('create')) { // Create Customer (Admin)
            if (this.customerConfigService.customerNumberAutoIncrementEnabled) {
                firstValueFrom(this.customerService.getNextCustomerNumber()).then(customerNumber => {
                    this.customerNumber.setValue(customerNumber);
                });
            }

            void this.setupHouseholdSize();

        } else { // Edit my profile
            this.editingMyProfile = true;

            this.customerService.getMe().pipe().subscribe(customer => {
                this.cards = customer.cards ?? [];
                this.setupFormToEditMyProfile(customer);
            });
        }

        this.disableFieldsWithoutClientManagement();
    }

    ngOnDestroy(): void {
        this.modalService.dismissAll('destroy');

        this.langChangeSubscription.unsubscribe();
    }

    back(): void {
        this.location.back();
    }

    // The configuration is loaded asynchronously, so it may still be missing after a reload of this page
    private async setupHouseholdSize(): Promise<void> {
        await firstValueFrom(this.customerConfigService.configurationLoadedMessage.pipe(filter(isLoaded => isLoaded)));

        if (this.customerConfigService.householdSizeRequired) {
            this.householdSizeDisplayed = true;
            this.householdSize.addValidators(Validators.required);
            this.householdSize.updateValueAndValidity();

            // An existing customer may have no household size yet: show why the form cannot be saved
            if (this.customerId) {
                this.householdSize.markAsTouched();
            }
        }
    }

    save(): void {
        if (this.editingMyProfile) {

            if (this.customerForm.valid) {
                const birthDate = DateUtils.ngbDateStructToDate(this.birthDate.value);
                birthDate?.setHours(0, 0, 0, 0);

                const customerMeInputDto: CustomerInputDto = new CustomerInputDto({
                    id: this.id.value,
                    title: this.title.value as Title,
                    firstName: this.firstname.value,
                    lastName: this.lastname.value,
                    email: this.email.value,
                    birthDate,
                    street: this.street.value,
                    postalCode: this.postalCode.value.toString(),
                    city: this.city.value,
                    country: this.country.value,
                    phoneMobile: this.mobilePhone.value,
                    phonePrivate: this.phone.value,
                    phoneProfessional: this.businessPhone.value,
                });

                this.customerService.updateMe(customerMeInputDto).pipe().subscribe(() => {
                    this.notificationsService.success({title: 'user-creation.notifications.updateMeSuccess'});
                });

            } else {
                window.scroll(0, 0);
            }

        } else {

            if (this.customerForm.valid) {
                const birthDate = DateUtils.ngbDateStructToDate(this.birthDate.value);
                birthDate?.setHours(0, 0, 0, 0);

                const customerInputDto: CustomerInputDto = new CustomerInputDto({
                    id: this.id.value,
                    title: this.title.value as Title,
                    firstName: this.firstname.value,
                    lastName: this.lastname.value,
                    email: this.email.value,
                    birthDate,
                    street: this.street.value,
                    postalCode: this.postalCode.value.toString(),
                    city: this.city.value,
                    country: this.country.value,
                    phoneMobile: this.mobilePhone.value,
                    phonePrivate: this.phone.value,
                    phoneProfessional: this.businessPhone.value,
                    customRoles: this.customRoles.value ?? [],
                    sites: this.sites.value ?? [],
                    accountEnabled: this.accountEnabled.value,
                    customerNumber: this.customerNumber.value,
                    attentionTo: this.attentionTo.value,
                    householdSize: this.householdSize.value,
                    cards: this.cards,
                    comment: this.comment.value,
                    internalField1: this.internalField1.value,
                    internalField2: this.internalField2.value,
                    internalField3: this.internalField3.value,
                    internalField4: this.internalField4.value,
                    internalField5: this.internalField5.value
                });

                if (!customerInputDto.id) {
                    this.customerService.createCustomer(customerInputDto).pipe().subscribe(() => {
                        this.notificationsService.success({title: 'user-creation.notifications.addSuccess'});
                        this.back();
                    });

                } else {
                    if (this.email.dirty && this.firstname.dirty && this.lastname.dirty) {
                       this.confirmService.confirm({
                            titleKey: 'users.form.reallocation-confirm.title',
                            messageKey: 'users.form.reallocation-confirm.message'
                        }).then(result => {
                            if (result === ConfirmModalService.yes) {
                                this.customerService.updateCustomer(customerInputDto.id, customerInputDto).pipe().subscribe(() => {
                                    this.notificationsService.success({title: 'user-creation.notifications.updateSuccess'});
                                    this.customerForm.markAsPristine();
                                });
                            }
                        });
                    } else {
                        this.customerService.updateCustomer(customerInputDto.id, customerInputDto).pipe().subscribe(() => {
                            this.notificationsService.success({title: 'user-creation.notifications.updateSuccess'});
                            this.customerForm.markAsPristine();
                        });
                    }
                }
            } else {
                window.scroll(0, 0);
            }
        }
    }

    addRfidCard(): void {
        const modal = this.modalService.open(CustomerRfidCardModalComponent, {centered: true});
        modal.componentInstance.customerId = this.customerId;
        modal.componentInstance.rfidCards = this.cards;
        modal.result
            .then(rfidCards => this.cards = rfidCards, () => { /* catch the rejection */
            });
    }

    unassignRfidCard(rfid: CardDto): void {
        this.confirmService.confirm({titleKey: 'common.confirmModal.title.unassign'}).then(result => {
            if (result === ConfirmModalService.yes) {
                const index = this.cards.indexOf(rfid);

                if (!this.customerId) {
                    if (index > -1) {
                        this.cards.splice(index, 1);
                    }

                } else {
                    this.cardService.unassignRfidCard(rfid).pipe().subscribe(() => {
                        if (index > -1) {
                            this.cards.splice(index, 1);
                        }
                        this.notificationsService.success({message: 'cards.notifications.unassignSuccess'});
                    });
                }
            }
        });
    }

    resetPassword(): void {
        const passwordResetUrl = `https://${environment.b2cDomainName}.b2clogin.com/${environment.b2cDomainName}.onmicrosoft.com/${environment.b2cResetPasswordPolicy}`;
        this.authService.loginRedirect({authority: passwordResetUrl, scopes: []});
    }

    deleteCustomer(): void {
        const modal = this.modalService.open(CustomerDeleteConfirmationModalComponent, {centered: true});
        modal.componentInstance.customerId = this.customerId;
        modal.result
            .then(value => {
                if (value === ConfirmModalService.yes) {
                    this.customerService.deleteCustomer(this.customerId).pipe().subscribe({
                        next: () => {
                            this.notificationsService.success({ title: 'users.notifications.deleteSuccess' });
                            void this.router.navigate(['users/customers']);
                        },
                        error: () => this.notificationsService.error({ title: 'users.notifications.deleteError' })
                    });
                }
            }, () => { /* catch the rejection */
            });
    }

    async setAuditsPage(page: any): Promise<void> {
        this.auditCurrentPage = page.offset;

        await this.fetchAudits();
    }

    getRowClass(row: CustomerAuditEntryDto): string {
        return row.changeType == CustomerAuditEntryPropertyChangeType.PropertyChange && row.properties.length > 1 ?
            CssConstants.max150pxRowHeightClass : CssConstants.unlimitedRowHeightClass;
    }

    siteName(site: SiteDto): string {
        return SiteUtils.siteName(site, this.crtLang);
    }

    siteDescription(site: SiteDto): string | undefined {
        return SiteUtils.siteDescription(site, this.crtLang);
    }

    async fetchAudits() {
        this.pagedAudits = await firstValueFrom(this.customerService.getCustomerAuditEntries(this.customerId, new PageableRequestDto({page: this.auditCurrentPage + 1, pageSize: this.auditPageSize})));
    }

    updateRfidCard(rfidCard: CardDto): void {
        const modal = this.modalService.open(RfidCardModalComponent, {centered: true});

        modal.componentInstance.rfidCard = rfidCard;
        modal.componentInstance.cardNumberDisabled = true;
        modal.componentInstance.uidDisabled = true;

        modal.result.then(async value => {
            if (value === 'success') {
                this.cards = (await firstValueFrom(this.customerService.getCustomer(this.customerId))).cards;
                this.notificationsService.success({title: 'cards.notifications.updateSuccess'});
            }
        });
    }

    isVirtualCard(card: CardDto) : boolean {
        return card.cardState === CardState.Virtual;
    }

    async openVirtualCardQrCode() : Promise<void> {
        let virtualCard: VirtualCardDto;

        if (this.editingMyProfile) {
            virtualCard = await firstValueFrom(this.cardService.getMyVirtualCard());
        } else {
            virtualCard = await firstValueFrom(this.cardService.getVirtualCardForCustomer(this.customerId));
        }

        const modal = this.modalService.open(VirtualCardQrCodeViewComponent, {centered: true});
        modal.componentInstance.qrData = virtualCard.qrCodeData;
        modal.componentInstance.cardNumber = virtualCard.cardNumber;
    }

    async regenerateVirtualCard() : Promise<void> {
        const result = await this.confirmService.confirm({
            titleKey: 'common.confirmModal.title.regenerate',
            infoKey: 'common.confirmModal.message.regenerateVirtualCard'
        });

        if (result === ConfirmModalService.yes) {

            if (this.editingMyProfile) {
                await firstValueFrom(this.cardService.regenerateMyVirtualCard());
            } else {
                await firstValueFrom(this.cardService.regenerateVirtualCardForCustomer(this.customerId));
            }

            await this.fetchCustomerCards();

            this.notificationsService.success({title: 'cards.notifications.regenerateVirtualCardSuccess'});
        }
    }

    private setupFormToEditMyProfile(customerMeInput: CustomerMeInputDto): void {
        this.id.setValue(customerMeInput.id);
        this.title.setValue(customerMeInput.title);
        this.firstname.setValue(customerMeInput.firstName);
        this.lastname.setValue(customerMeInput.lastName);
        this.birthDate.setValue(DateUtils.dateToNgbDateStruct(customerMeInput.birthDate));
        this.email.setValue(customerMeInput.email);
        this.street.setValue(customerMeInput.street);
        this.postalCode.setValue(customerMeInput.postalCode);
        this.city.setValue(customerMeInput.city);
        this.country.setValue(customerMeInput.country);
        this.phone.setValue(customerMeInput.phonePrivate);
        this.mobilePhone.setValue(customerMeInput.phoneMobile);
        this.businessPhone.setValue(customerMeInput.phoneProfessional);
        this.sites.setValue(SiteUtils.sortByName(customerMeInput.sites ?? [], this.crtLang));

        // Remove unused controls
        this.customerForm.removeControl('customerNumber');
        this.customerForm.removeControl('customRoles');
        this.customerForm.removeControl('attentionTo');
        this.customerForm.removeControl('accountEnabled');
        this.customerForm.removeControl('comment');
        this.customerForm.removeControl('internalField1');
        this.customerForm.removeControl('internalField2');
        this.customerForm.removeControl('internalField3');
        this.customerForm.removeControl('internalField4');
        this.customerForm.removeControl('internalField5');
    }

    private async setupFormToEditFullProfile(customerInput: CustomerInputDto): Promise<void> {
        this.id.setValue(customerInput.id);
        this.title.setValue(customerInput.title);
        this.accountEnabled.setValue(customerInput.accountEnabled);
        this.firstname.setValue(customerInput.firstName);
        this.lastname.setValue(customerInput.lastName);

        this.birthDate.setValue(DateUtils.dateToNgbDateStruct(customerInput.birthDate));
        this.email.setValue(customerInput.email);
        this.street.setValue(customerInput.street);
        this.postalCode.setValue(customerInput.postalCode);
        this.city.setValue(customerInput.city);
        this.country.setValue(customerInput.country);
        this.phone.setValue(customerInput.phonePrivate);
        this.mobilePhone.setValue(customerInput.phoneMobile);
        this.businessPhone.setValue(customerInput.phoneProfessional);
        this.customerNumber.setValue(customerInput.customerNumber);
        this.attentionTo.setValue(customerInput.attentionTo);
        this.householdSize.setValue(customerInput.householdSize);
        this.comment.setValue(customerInput.comment);
        this.internalField1.setValue(customerInput.internalField1);
        this.internalField2.setValue(customerInput.internalField2);
        this.internalField3.setValue(customerInput.internalField3);
        this.internalField4.setValue(customerInput.internalField4);
        this.internalField5.setValue(customerInput.internalField5);

        this.customRoles.setValue(customerInput.customRoles ?? []);
        this.sites.setValue(customerInput.sites ?? []);
    }

    private async fetchCustomerCards() : Promise<void> {
        let customer: CustomerInputDto;
        if (this.editingMyProfile) {
            customer = await firstValueFrom(this.customerService.getMe());
        } else {
            customer = await firstValueFrom(this.customerService.getCustomer(this.customerId));
        }

        this.cards = customer.cards ?? [];
    }

    private disableFieldsWithoutClientManagement(): void {
        if (!this.appPermissionService.hasPermission(PermissionKeys.gestionClients)) {
            this.title.disable();
            this.firstname.disable();
            this.lastname.disable();
            this.birthDate.disable();
            this.street.disable();
            this.postalCode.disable();
            this.city.disable();
            this.country.disable();
        }
    }

    get id(): FormControl<string | null> {
        return this.customerForm.get('id') as FormControl<string | null>;
    }

    get firstname(): FormControl<string | null> {
        return this.customerForm.get('firstname') as FormControl<string | null>;
    }

    get lastname(): FormControl<string | null> {
        return this.customerForm.get('lastname') as FormControl<string | null>;
    }

    get title(): FormControl<Title | null> {
        return this.customerForm.get('title') as FormControl<Title | null>;
    }

    get accountEnabled(): FormControl<boolean | null> {
        return this.customerForm.get('accountEnabled') as FormControl<boolean | null>;
    }

    get street(): FormControl<string | null> {
        return this.customerForm.get('street') as FormControl<string | null>;
    }

    get city(): FormControl<string | null> {
        return this.customerForm.get('city') as FormControl<string | null>;
    }

    get country(): FormControl<string | null> {
        return this.customerForm.get('country') as FormControl<string | null>;
    }

    get postalCode(): FormControl<string | null> {
        return this.customerForm.get('postalCode') as FormControl<string | null>;
    }

    get birthDate(): FormControl<NgbDateStruct | null> {
        return this.customerForm.get('birthDate') as FormControl<NgbDateStruct | null>;
    }

    get email(): FormControl<string | null> {
        return this.customerForm.get('email') as FormControl<string | null>;
    }

    get phone(): FormControl<string | null> {
        return this.customerForm.get('phone') as FormControl<string | null>;
    }

    get mobilePhone(): FormControl<string | null> {
        return this.customerForm.get('mobilePhone') as FormControl<string | null>;
    }

    get businessPhone(): FormControl<string | null> {
        return this.customerForm.get('businessPhone') as FormControl<string | null>;
    }

    get customerNumber(): FormControl<string | null> {
        return this.customerForm.get('customerNumber') as FormControl<string | null>;
    }

    get customRoles(): FormControl<CustomRoleDto[]> {
        return this.customerForm.get('customRoles') as FormControl<CustomRoleDto[]>;
    }

    get sites(): FormControl<SiteDto[]> {
        return this.customerForm.get('sites') as FormControl<SiteDto[]>;
    }

    get attentionTo(): FormControl<string | null> {
        return this.customerForm.get('attentionTo') as FormControl<string | null>;
    }

    get householdSize(): FormControl<number | null> {
        return this.customerForm.get('householdSize') as FormControl<number | null>;
    }

    get comment(): FormControl<string | null> {
        return this.customerForm.get('comment') as FormControl<string | null>;
    }

    get internalField1(): FormControl<string | null> {
        return this.customerForm.get('internalField1') as FormControl<string | null>;
    }

    get internalField2(): FormControl<string | null> {
        return this.customerForm.get('internalField2') as FormControl<string | null>;
    }

    get internalField3(): FormControl<string | null> {
        return this.customerForm.get('internalField3') as FormControl<string | null>;
    }

    get internalField4(): FormControl<string | null> {
        return this.customerForm.get('internalField4') as FormControl<string | null>;
    }

    get internalField5(): FormControl<string | null> {
        return this.customerForm.get('internalField5') as FormControl<string | null>;
    }
}

export interface CustomerForm {
    id: FormControl<string | null>;
    title: FormControl<Title | null>;
    firstname: FormControl<string | null>;
    lastname: FormControl<string | null>;
    birthDate: FormControl<NgbDateStruct | null>;
    email: FormControl<string | null>;
    phone: FormControl<string | null>;
    mobilePhone: FormControl<string | null>;
    businessPhone: FormControl<string | null>;
    street: FormControl<string | null>;
    postalCode: FormControl<string | null>;
    city: FormControl<string | null>;
    country: FormControl<string | null>;
    customerNumber?: FormControl<string | null>;
    customRoles?: FormControl<CustomRoleDto[]>;
    sites?: FormControl<SiteDto[]>;
    attentionTo?: FormControl<string | null>;
    householdSize?: FormControl<number | null>;
    accountEnabled?: FormControl<boolean | null>;
    comment?: FormControl<string | null>;
    internalField1?: FormControl<string | null>;
    internalField2?: FormControl<string | null>;
    internalField3?: FormControl<string | null>;
    internalField4?: FormControl<string | null>;
    internalField5?: FormControl<string | null>;
}
